/**
 * Telegram, set up from the desktop app with one tap: "Connect Telegram" opens the Vephus bot in
 * Telegram with a one-time code, the person presses Start there, and the chat is linked. From then
 * on what they write to the bot goes to their own agent, and the answer comes back to the chat.
 *
 * Offered only with Claude Code, which is what remote control drives today (see AgentMatrix).
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Send, Unlink } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { openExternal } from '../lib/desktop'
import { Badge, ErrorBanner, Panel } from './primitives'

export function TelegramPanel() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const [waiting, setWaiting] = useState(false)

  const { data: status, isError } = useQuery({
    queryKey: ['telegram'],
    queryFn: api.telegram.status,
    retry: false,
    // While the person is in Telegram pressing Start, notice as soon as the link is made.
    refetchInterval: (query) => (waiting && !query.state.data?.linked ? 3_000 : false),
  })

  const connect = useMutation({
    mutationFn: async () => {
      const { url } = await api.telegram.link()
      await openExternal(url)
    },
    onSuccess: () => setWaiting(true),
  })

  const disconnect = useMutation({
    mutationFn: api.telegram.unlink,
    onSuccess: () => {
      setWaiting(false)
      void queryClient.invalidateQueries({ queryKey: ['telegram'] })
    },
  })

  return (
    <Panel title={t('telegram.title')}>
      <div className="stack">
        <p className="small muted" style={{ margin: 0 }}>{t('telegram.what')}</p>

        {isError || (status && !status.available) ? (
          <p className="small" style={{ margin: 0 }}>{t('telegram.unavailable')}</p>
        ) : status?.linked ? (
          <div className="row row--wrap">
            <Badge tone="ok"><Check size={11} /> {t('telegram.linked', { who: status.username ? `@${status.username}` : '' })}</Badge>
            <span className="tiny muted">{t('telegram.howToUse')}</span>
            <button type="button" className="btn btn--sm btn--ghost" onClick={() => disconnect.mutate()} disabled={disconnect.isPending}>
              <Unlink size={13} /> {t('telegram.disconnect')}
            </button>
          </div>
        ) : (
          <div className="row row--wrap">
            <button type="button" className="btn btn--primary" onClick={() => connect.mutate()} disabled={connect.isPending || !status}>
              <Send size={14} /> {t('telegram.connect')}
            </button>
            {waiting && <span className="small muted">{t('telegram.waiting')}</span>}
          </div>
        )}
        {connect.isError && <ErrorBanner message={t('error.generic')} />}
      </div>
    </Panel>
  )
}
